/**
 * Why a move was good or bad, in facts rather than adjectives.
 *
 * The review could already say *what* the engine preferred. On its own that is
 * an instruction, not an explanation: "column 4 was better" teaches a player to
 * copy an engine they do not have in front of them next game. What teaches is
 * the reason — you left a line open, they were one move from winning, that move
 * would have given you two ways to finish at once.
 *
 * Every fact in here is **proven by replaying the position**, never inferred
 * from the evaluation score. A score says a move is worth 300 points; it cannot
 * say why, and prose invented to match a number is the same lie as an accuracy
 * percentage with a decimal point on it. Where nothing can be proven, this
 * module returns nothing and the screen stays quiet.
 *
 * Spec-driven like the rest of `games/`: a "line" is whatever `terminal.win`
 * declares, and a threat is discovered by asking the interpreter, so an authored
 * 9x7 game is explained by this same code.
 *
 * Boundary contract, matching `spec/interpreter.ts`:
 *  - pure: no I/O, no clock, no randomness;
 *  - inputs are treated as frozen and never mutated;
 *  - total: every loop is bounded by the board's own width, and an in-type spec
 *    that makes no sense yields no facts rather than a throw.
 */
import { applyMove, legalMoves } from '../spec/interpreter.ts';
import type { Move, SeatOf, State } from '../spec/interpreter.ts';
import type { RulesSpec } from '../spec/schema.ts';
import { enumerateWinWindows } from './eval.ts';
import type { WinWindow } from './eval.ts';

/**
 * One provable thing a move does.
 *
 * Ordered loosely from most to least decisive. Each carries the parameters a
 * player would need to check the claim on the board themselves — an explanation
 * they cannot verify is just a louder assertion.
 */
export type MoveFact =
  /** Completes a line. The game ends here. */
  | { readonly kind: 'wins_now' }
  /** Occupies the cell the opponent needed to win on their next turn. */
  | { readonly kind: 'blocks_win'; readonly param: number }
  /**
   * Leaves two or more ways to win next turn. The opponent can answer one, so
   * this is the fact worth teaching: it is how a game is actually won.
   */
  | { readonly kind: 'makes_double_threat'; readonly params: readonly number[] }
  /** Leaves exactly one way to win next turn, which forces a reply. */
  | { readonly kind: 'makes_threat'; readonly param: number }
  /** Hands the opponent a move that wins at once. */
  | { readonly kind: 'allows_win'; readonly param: number }
  /** Hands the opponent two or more ways to win, which cannot all be blocked. */
  | { readonly kind: 'allows_double_threat'; readonly params: readonly number[] }
  /*
   * Below here: positional facts, for the quiet moves.
   *
   * Most moves in a game threaten nothing and give nothing away, and a review
   * that goes blank on them says "Good" with no way to learn what was good
   * about it. These are still *counted*, never estimated — every one is a
   * tally over the winning windows the spec itself declares, so a player can
   * check it on the board.
   */
  /** Joins `count` of the mover's own pieces inside a line still open to them. */
  | { readonly kind: 'builds_line'; readonly count: number; readonly length: number }
  /** Cuts `count` lines the opponent was still able to complete through this square. */
  | { readonly kind: 'denies_lines'; readonly count: number }
  /**
   * How much of the board this square reaches: `lines` winning lines run
   * through it, against `bestAvailable` for the best square the mover could
   * actually have played this turn.
   *
   * Always available, which is the point — it is what makes an opening move
   * explicable at all. It is "take the centre" stated as a count rather than as
   * folklore, and it degrades correctly on a board whose best square is not in
   * the middle, because nothing here assumes one.
   *
   * Measured against the *playable* squares, not the whole board: on a gravity
   * board the richest cell is somewhere in the middle of the air, and holding a
   * player's move up against a square they could not reach would be a true
   * sentence that teaches nothing.
   */
  | { readonly kind: 'square_reach'; readonly lines: number; readonly bestAvailable: number };

/**
 * Moves that would win for `seat` **if it were their turn**.
 *
 * The turn is overridden deliberately. A threat is by definition a thing that
 * has not happened yet: "if nobody stops me, this wins". The interpreter has no
 * other way to be asked that question, and overriding `toMove` on a copy is
 * honest — `applyMove` is pure, reads the seat it is given, and the real state
 * is never touched.
 *
 * Returns at most `cols` entries, so callers can count on it being small.
 */
export function winningMovesFor<S extends RulesSpec>(
  rules: S,
  state: State<S>,
  seat: SeatOf<S>,
): readonly number[] {
  if (state.status.kind !== 'in_play') return [];

  const hypothetical: State<S> = { ...state, toMove: seat };
  const wins: number[] = [];

  for (const move of legalMoves(rules, hypothetical)) {
    const applied = applyMove(rules, hypothetical, seat, move);
    if (!applied.ok) continue;
    const status = applied.state.status;
    if (status.kind === 'win' && status.winner === seat) wins.push(move.param);
  }

  return wins;
}

/**
 * Windows and per-cell lookups, cached per spec.
 *
 * `enumerateWinWindows` walks the whole board for every condition and
 * direction. Review calls the explainer once per ply, and every ply of a game
 * shares one spec, so recomputing it each time would be the most expensive
 * thing in a module whose whole point is being cheap next to the search.
 *
 * Keyed by spec identity: specs are frozen data passed down from the registry,
 * so the same object really is the same rules.
 */
const WINDOW_CACHE = new WeakMap<
  RulesSpec,
  {
    readonly windows: readonly WinWindow[];
    /** Windows through each cell. */
    readonly byCell: ReadonlyMap<number, readonly WinWindow[]>;
    /** The highest window count any single cell reaches. */
    readonly busiest: number;
  }
>();

function windowIndex(rules: RulesSpec) {
  const cached = WINDOW_CACHE.get(rules);
  if (cached) return cached;

  const windows = enumerateWinWindows(rules);
  const byCell = new Map<number, WinWindow[]>();
  for (const window of windows) {
    for (const cell of window) {
      const list = byCell.get(cell);
      if (list) list.push(window);
      else byCell.set(cell, [window]);
    }
  }

  let busiest = 0;
  for (const list of byCell.values()) {
    if (list.length > busiest) busiest = list.length;
  }

  const entry = { windows, byCell, busiest };
  WINDOW_CACHE.set(rules, entry);
  return entry;
}

/**
 * Positional facts: what a quiet move did, counted over the winning windows.
 *
 * Read from the board *after* the move, comparing against the board before it,
 * so every number here is a tally of real cells rather than a judgement.
 */
function positionalFacts<S extends RulesSpec>(
  rules: S,
  before: State<S>,
  after: State<S>,
  seat: SeatOf<S>,
  cell: number,
): readonly MoveFact[] {
  const { byCell } = windowIndex(rules);
  const through = byCell.get(cell) ?? [];
  if (through.length === 0) return [];

  const empty = rules.cells.empty;
  const facts: MoveFact[] = [];

  // The best line this piece is now part of: how many of the mover's own
  // pieces sit in a window that still has no opponent piece in it.
  let bestOwn = 0;
  let bestLength = 0;
  for (const window of through) {
    let own = 0;
    let blocked = false;
    for (const index of window) {
      const occupant = after.board[index];
      if (occupant === empty) continue;
      if (occupant === seat) own += 1;
      else {
        blocked = true;
        break;
      }
    }
    if (blocked) continue;
    if (own > bestOwn) {
      bestOwn = own;
      bestLength = window.length;
    }
  }
  // Two or more, because "this piece is in a line of one" is not a fact worth
  // anyone's attention.
  if (bestOwn >= 2) facts.push({ kind: 'builds_line', count: bestOwn, length: bestLength });

  // Lines the opponent could still have completed through this square, which
  // this piece has just ended. Counted on the board *before* the move: after
  // it, the square is taken and the possibility is invisible.
  let denied = 0;
  for (const window of through) {
    let theirs = 0;
    let mine = 0;
    for (const index of window) {
      const occupant = before.board[index];
      if (occupant === empty) continue;
      if (occupant === seat) mine += 1;
      else theirs += 1;
    }
    // Live for them, and worth naming only if they had actually started it.
    if (mine === 0 && theirs > 0) denied += 1;
  }
  if (denied > 0) facts.push({ kind: 'denies_lines', count: denied });

  // Always last, always present: the square's own reach, against the best the
  // mover could actually have reached this turn. Every other fact here is
  // conditional, and a move with none of them would otherwise leave the review
  // with nothing to say about a perfectly reasonable move.
  let bestAvailable = 0;
  for (const option of legalMoves(rules, before)) {
    const landed = applyMove(rules, before, seat, option);
    if (!landed.ok) continue;
    const reach = byCell.get(landed.primary)?.length ?? 0;
    if (reach > bestAvailable) bestAvailable = reach;
  }
  facts.push({ kind: 'square_reach', lines: through.length, bestAvailable });

  return facts;
}

/**
 * What one move does to the position, proven move by move.
 *
 * `before` is the position with `seat` to move; `move` is the move being
 * explained. Facts about what the mover gains are listed before facts about
 * what the opponent gains, because that is the order a player reads their own
 * move in.
 */
export function factsFor<S extends RulesSpec>(
  rules: S,
  before: State<S>,
  seat: SeatOf<S>,
  move: Move,
): readonly MoveFact[] {
  const applied = applyMove(rules, before, seat, move);
  if (!applied.ok) return [];

  const after = applied.state;
  const facts: MoveFact[] = [];

  const status = after.status;
  if (status.kind === 'win' && status.winner === seat) {
    // Nothing after this matters: there is no next turn to threaten anybody in.
    return Object.freeze([{ kind: 'wins_now' }]);
  }

  const opponent = after.toMove;

  // Did this move take a square the opponent needed? Asked of the position
  // *before* the move, because afterwards the square is gone and the threat it
  // carried is invisible.
  const theirThreatsBefore = winningMovesFor(rules, before, opponent);
  if (theirThreatsBefore.includes(move.param)) {
    facts.push({ kind: 'blocks_win', param: move.param });
  }

  // What the mover is threatening now.
  const mine = winningMovesFor(rules, after, seat);
  if (mine.length >= 2) facts.push({ kind: 'makes_double_threat', params: Object.freeze(mine) });
  else if (mine.length === 1 && mine[0] !== undefined) {
    facts.push({ kind: 'makes_threat', param: mine[0] });
  }

  // What it hands over. Only moves the opponent can actually play count, so
  // this asks for their real legal replies rather than reusing the hypothetical
  // above.
  const theirs: number[] = [];
  for (const reply of legalMoves(rules, after)) {
    const replied = applyMove(rules, after, opponent, reply);
    if (!replied.ok) continue;
    const outcome = replied.state.status;
    if (outcome.kind === 'win' && outcome.winner === opponent) theirs.push(reply.param);
  }
  if (theirs.length >= 2) facts.push({ kind: 'allows_double_threat', params: Object.freeze(theirs) });
  else if (theirs.length === 1 && theirs[0] !== undefined) {
    facts.push({ kind: 'allows_win', param: theirs[0] });
  }

  // Positional facts last, so a tactic always outranks them. They are what the
  // quiet majority of moves have to say for themselves, and without them a
  // "Good" verdict is a gold star with no lesson attached.
  facts.push(...positionalFacts(rules, before, after, seat, applied.primary));

  return Object.freeze(facts);
}

/**
 * The reason a move was not the best one, as a pair of proven halves.
 *
 * Both sides are needed to answer "why". What the played move cost is only half
 * the lesson; the other half is what the better move was actually *for*.
 */
export interface MoveReasoning {
  /** Facts about the move that was played. */
  readonly played: readonly MoveFact[];
  /** Facts about the engine's move. Empty when it is the same move. */
  readonly best: readonly MoveFact[];
  /**
   * True when the mover had a move that won on the spot.
   *
   * Kept as its own flag rather than left to be inferred from `best`, because
   * "you could have won right there" outranks every other thing the screen
   * could say about a move, and a missed win is not always the engine's pick —
   * with several wins available the engine names one and the player may have
   * been one square from another.
   */
  readonly missedWin: boolean;
}

/**
 * Explain one played move against the engine's choice.
 *
 * Cheap by construction: a handful of `applyMove` calls over a board's width,
 * against a search budget measured in tens of milliseconds. It is called once
 * per ply, next to a search that costs a thousand times more.
 */
export function explainMove<S extends RulesSpec>(
  rules: S,
  before: State<S>,
  seat: SeatOf<S>,
  played: Move,
  best: Move,
): MoveReasoning {
  const playedFacts = factsFor(rules, before, seat, played);
  const samePlay = played.param === best.param;
  const bestFacts = samePlay ? [] : factsFor(rules, before, seat, best);

  // A win that was on the board and was not taken. `wins_now` among the played
  // facts means it *was* taken, so the two can never both be true.
  const winsAvailable = winningMovesFor(rules, before, seat);
  const missedWin =
    winsAvailable.length > 0 && !playedFacts.some((fact) => fact.kind === 'wins_now');

  return Object.freeze({
    played: playedFacts,
    best: Object.freeze(bestFacts),
    missedWin,
  });
}
